package com.wintermar.inspection.feature.data.repository

import com.apollographql.apollo.ApolloClient
import com.apollographql.apollo.api.Optional
import com.wintermar.inspection.AppDatabase
import com.wintermar.inspection.core.data.AppSetting
import com.wintermar.inspection.core.domain.FlowResult
import com.wintermar.inspection.core.domain.Result
import com.wintermar.inspection.core.domain.handleAsFlow
import com.wintermar.inspection.core.ui.component.datetime.util.DateTimeUtil
import com.wintermar.inspection.core.ui.component.image.util.FileModel
import com.wintermar.inspection.feature.data.local.db.SqliteCompat
import com.wintermar.inspection.feature.data.remote.mapper.inspection.toFileModel
import com.wintermar.inspection.feature.data.remote.mapper.inspection.toInspectionListFromApi
import com.wintermar.inspection.feature.data.remote.mapper.inspection.toInspectionModel
import com.wintermar.inspection.feature.data.remote.mapper.inspection.toInspectionModelFromApi
import com.wintermar.inspection.feature.data.remote.mapper.inspection.toListInspectionModel
import com.wintermar.inspection.feature.data.remote.request.HeaderRequest
import com.wintermar.inspection.feature.domain.model.VesselModel
import com.wintermar.inspection.feature.domain.model.inspection.InspectionLandingModel
import com.wintermar.inspection.feature.domain.model.inspection.InspectionModel
import com.wintermar.inspection.feature.domain.model.template.TemplateOptionModel
import com.wintermar.inspection.feature.domain.repository.InspectionRepository
import com.wintermar.inspection.graphql.ApproveInspectionMutation
import com.wintermar.inspection.graphql.CompleteInspectionMutation
import com.wintermar.inspection.graphql.DeleteInspectionMutation
import com.wintermar.inspection.graphql.DeleteInspectionReportMutation
import com.wintermar.inspection.graphql.GenerateInspectionReportPdfQuery
import com.wintermar.inspection.graphql.InspectionByIdWithMediaUrlQuery
import com.wintermar.inspection.graphql.InspectionListQuery
import com.wintermar.inspection.graphql.StartInspectionMutation
import com.wintermar.inspection.graphql.type.ApproveInspectionCommandInput
import com.wintermar.inspection.graphql.type.CompleteInspectionCommandInput
import com.wintermar.inspection.graphql.type.DeleteInspectionCommandInput
import com.wintermar.inspection.graphql.type.DeleteInspectionReportCommandInput
import com.wintermar.inspection.graphql.type.GenerateInspectionReportPdfQueryInput
import com.wintermar.inspection.graphql.type.GetInspectionByIdWithMediaUrlQueryInput
import com.wintermar.inspection.graphql.type.GetInspectionListQueryInput
import com.wintermar.inspection.graphql.type.InspectionStatus
import com.wintermar.inspection.graphql.type.StartInspectionCommandInput
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.flow
import kotlinx.coroutines.flow.flowOn
import kotlinx.coroutines.withContext

class InspectionRepositoryImpl(
    private val apolloClient: ApolloClient,
    private val appDatabase: AppDatabase,
    private val appSetting: AppSetting
) : InspectionRepository {
    override fun getInspections(
        vesselList: List<VesselModel>,
        page: Int,
        pageSize: Int
    ): Flow<FlowResult<InspectionLandingModel>> {
        return handleAsFlow(
            request = {
                apolloClient
                    .query(
                        InspectionListQuery(
                            GetInspectionListQueryInput(
                                page = page,
                                pageSize = pageSize,
                                status = Optional.present(
                                    listOf(
                                        InspectionStatus.IN_PROGRESS,
                                        InspectionStatus.COMPLETED
                                    )
                                )
                            )
                        )
                    )
                    .addHttpHeader(
                        HeaderRequest.OPERATION,
                        InspectionListQuery.OPERATION_NAME
                    )
                    .execute()
            }
        ) { response ->
            val data = response.data?.inspectionList
            InspectionLandingModel(
                data = data?.data?.toInspectionListFromApi(vesselList) ?: emptyList(),
                total = data?.totalCount ?: 0
            )
        }
    }

    override fun getInspectionsFromLocalDb(
        page: Int,
        pageSize: Int
    ): Flow<FlowResult<InspectionLandingModel>> {
        return flow {
            emit(FlowResult.Loading(showLoading = true))
            try {
                val offset = (page - 1) * pageSize.toLong()
                val result = appDatabase.transactionWithResult {
                    val queries = appDatabase.inspectionTableQueries
                    val items = queries.getInspectionsPaginated(
                        limit = pageSize.toLong(),
                        offset = offset,
                        userId = appSetting.userId ?: ""
                    ).executeAsList()
                        .toListInspectionModel()
                    val total =
                        queries.countInspections(appSetting.userId ?: "").executeAsOne().toInt()
                    Pair(items, total)
                }
                emit(
                    FlowResult.Success(
                        InspectionLandingModel(
                            data = result.first,
                            total = result.second
                        )
                    )
                )
            } catch (e: Exception) {
                emit(FlowResult.Error(e))
            }
            emit(FlowResult.Loading(showLoading = false))
        }.flowOn(Dispatchers.Default)
    }

    override suspend fun insertInspection(inspection: InspectionModel): Result<Unit> {
        return withContext(Dispatchers.Default) {
            try {
                appDatabase.transaction {
                    val executableQuery = SqliteCompat.selectInsertImplementation(
                        appDatabase = appDatabase,
                        upsert = appDatabase.inspectionTableQueries::insertInspection,
                        replace = appDatabase.inspectionTableQueries::insertReplaceInspection
                    )

                    executableQuery(
                        inspection.id,
                        inspection.templateId,
                        inspection.templateName,
                        inspection.vesselName,
                        inspection.totalAnswer.toLong(),
                        inspection.totalQuestion.toLong(),
                        inspection.lastUpdated,
                        inspection.status.name,
                        appSetting.userId ?: "",
                        inspection.conducted
                    )
                }
                Result.Success(Unit)
            } catch (e: Exception) {
                Result.Error(e)
            }
        }
    }

    override fun getInspectionByIdFromLocalDb(id: String): Flow<FlowResult<InspectionModel>> {
        return flow {
            emit(FlowResult.Loading(showLoading = true))
            try {
                val inspectionTable =
                    appDatabase.inspectionTableQueries.getInspectionById(id).executeAsOne()
                emit(FlowResult.Success(inspectionTable.toInspectionModel()))
            } catch (e: Exception) {
                emit(FlowResult.Error(e))
            }
            emit(FlowResult.Loading(showLoading = false))
        }.flowOn(Dispatchers.Default)
    }

    override fun getInspectionById(
        id: String,
        templateOptions: List<TemplateOptionModel>,
        userFullName: String
    ): Flow<FlowResult<InspectionModel>> {
        return handleAsFlow(
            request = {
                apolloClient
                    .query(
                        InspectionByIdWithMediaUrlQuery(
                            GetInspectionByIdWithMediaUrlQueryInput(id)
                        )
                    )
                    .addHttpHeader(
                        HeaderRequest.OPERATION,
                        InspectionByIdWithMediaUrlQuery.OPERATION_NAME
                    )
                    .execute()
            }
        ) { response ->
            response.data?.inspectionByIdWithMediaUrl?.toInspectionModelFromApi(
                templateOptions = templateOptions,
                userFullName = userFullName
            ) ?: InspectionModel()
        }
    }

    override suspend fun updateProgress(
        inspectionId: String,
        totalAnswer: Int,
        lastUpdated: String,
        vesselName: String?
    ): Result<Unit> {
        return withContext(Dispatchers.Default) {
            try {
                appDatabase.transaction {
                    appDatabase.inspectionTableQueries.updateProgress(
                        id = inspectionId,
                        totalAnswer = totalAnswer.toLong(),
                        lastUpdated = lastUpdated,
                        vesselName = vesselName,
                    )
                }
                Result.Success(Unit)

            } catch (e: Exception) {
                Result.Error(e)
            }
        }
    }

    override fun startInspection(
        inspectionId: String,
        templateId: String,
        conductedAtIso: String
    ): Flow<FlowResult<Unit>> {
        return handleAsFlow(
            request = {
                apolloClient
                    .mutation(
                        StartInspectionMutation(
                            StartInspectionCommandInput(
                                id = inspectionId,
                                templateId = templateId,
                                conductedAt = conductedAtIso
                            )
                        )
                    )
                    .addHttpHeader(HeaderRequest.OPERATION, StartInspectionMutation.OPERATION_NAME)
                    .execute()
            }
        ) { response ->
            response.data?.startInspection?.status
        }
    }

    override fun completeInspection(inspectionId: String): Flow<FlowResult<Pair<InspectionStatus, String>>> {
        return handleAsFlow(
            request = {
                apolloClient
                    .mutation(
                        CompleteInspectionMutation(
                            CompleteInspectionCommandInput(inspectionId)
                        )
                    )
                    .addHttpHeader(
                        HeaderRequest.OPERATION,
                        CompleteInspectionMutation.OPERATION_NAME
                    )
                    .execute()
            }
        ) { response ->
            val data = response.data?.completeInspection
            Pair(
                data?.status ?: InspectionStatus.UNKNOWN__,
                data?.lastUpdatedAt.toString().ifEmpty { DateTimeUtil.getCurrentDateIsoUtc0() })
        }
    }

    override suspend fun updateStatus(
        inspectionId: String,
        status: InspectionStatus,
        lastUpdated: String
    ): Result<Unit> {
        return withContext(Dispatchers.Default) {
            try {
                appDatabase.transaction {
                    appDatabase.inspectionTableQueries.updateStatus(
                        id = inspectionId,
                        lastUpdated = lastUpdated,
                        status = status.name
                    )
                }
                Result.Success(Unit)

            } catch (e: Exception) {
                Result.Error(e)
            }
        }
    }

    override suspend fun deleteInspectionByIdFromLocalDb(id: String): Result<Unit> {
        return withContext(Dispatchers.Default) {
            try {
                appDatabase.transaction {
                    appDatabase.inspectionTableQueries.deleteInspectionById(id)
                }
                Result.Success(Unit)

            } catch (e: Exception) {
                Result.Error(e)
            }
        }
    }

    override fun deleteInspectionById(id: String): Flow<FlowResult<Unit>> {
        return handleAsFlow(
            request = {
                apolloClient
                    .mutation(
                        DeleteInspectionMutation(
                            DeleteInspectionCommandInput(
                                inspectionId = id
                            )
                        )
                    )
                    .addHttpHeader(
                        HeaderRequest.OPERATION,
                        DeleteInspectionMutation.OPERATION_NAME
                    )
                    .execute()
            }
        ) {
            // no-op
        }
    }

    override fun approveInspection(id: String): Flow<FlowResult<Unit>> {
        return handleAsFlow(
            request = {
                apolloClient
                    .mutation(
                        ApproveInspectionMutation(
                            ApproveInspectionCommandInput(
                                inspectionId = id
                            )
                        )
                    )
                    .addHttpHeader(
                        HeaderRequest.OPERATION,
                        ApproveInspectionMutation.OPERATION_NAME
                    )
                    .execute()
            }
        ) {
            // no-op
        }
    }

    override fun downloadInspectionReport(id: String): Flow<FlowResult<FileModel>> {
        return handleAsFlow(
            request = {
                apolloClient
                    .query(
                        GenerateInspectionReportPdfQuery(
                            GenerateInspectionReportPdfQueryInput(
                                inspectionId = id
                            )
                        )
                    )
                    .addHttpHeader(
                        HeaderRequest.OPERATION,
                        GenerateInspectionReportPdfQuery.OPERATION_NAME
                    )
                    .execute()
            }
        ) { response ->
            response.data?.generateInspectionReportPdf?.toFileModel()
                ?: throw IllegalStateException("Failed to generate inspection report PDF")
        }
    }

    override fun deleteInspectionReport(id: String): Flow<FlowResult<Unit>> {
        return handleAsFlow(
            request = {
                apolloClient
                    .mutation(
                        DeleteInspectionReportMutation(
                            DeleteInspectionReportCommandInput(
                                inspectionId = id
                            )
                        )
                    )
                    .addHttpHeader(
                        HeaderRequest.OPERATION,
                        DeleteInspectionReportMutation.OPERATION_NAME
                    )
                    .execute()
            }
        ) {
            // no-op
        }
    }
}
